import {
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import { AddCircle, MoreHoriz } from '@material-ui/icons';
import CreateIcon from '@material-ui/icons/Create';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import SearchIcon from '@material-ui/icons/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DeviceEnablingPopup from '../../../../components/device-enabling-popop/Device-Enabling-Popup';
import { HOTEL_CREATE, HOTEL_EDIT, HOTEL_VIEW } from '../../../../constants/PropertyConst';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_HOTELS,
} from '../../../../constants/RoleConsts';
import DeletePopupModel from '../../../../shared/delete-popup/Delete-Popup';
import TablePaginationActions from '../../../../shared/table-pagination/TablePaginationActions';
import { FormattingUtils } from '../../../../utils/DataFormattingUtils';
import { notificationManager } from '../../../../utils/NotificationUtils';
import {
  deleteHotelById,
  updateHotelDeviceModule,
  updateHotelUIBuilder,
} from '../../../../utils/PropertyAPIUtils';
import { getUserLevel } from '../../../../utils/RBACUtil';
import {
  allBrandsSelector,
  allGroupsSelector,
  fetchAllProperties,
  filteredHotelsSelector,
} from '../../propertySlice';

const BrandList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
    isPropertyAdmin = false,
  } = props;
  const dispatch = useDispatch();
  const groups = useSelector(allGroupsSelector);
  const brands = useSelector(allBrandsSelector);
  const originalHotels = useSelector(filteredHotelsSelector);
  const [hotels, setHotels] = useState([]);
  const initialState = {
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    anchorEl: null,
    deviceEnabling: false,
  };
  const [selectedValue, setSelectedValue] = useState(initialState);
  const [userRole, setUserRole] = useState('');
  const [tempSearchText, setTempSearchText] = useState('');
  const [searchButton, setSearchButton] = useState(true);

  (async () => {
    try {
      const role = await getUserLevel();
      setUserRole(role);
    } catch (e) {
      console.error('User role retrieval failed');
    }
  })();

  useEffect(() => {
    if (originalHotels) {
      setHotels(originalHotels);
      setTempSearchText('');
      setSearchButton(true);
    }
  }, [originalHotels]);

  useEffect(() => {
    if (hotels?.length === 0) return;
    const totalItems = hotels?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [hotels?.length, rowsPerPage, page, onsetPage]);

  const setSearchText = (searchText) => {
    setTempSearchText(searchText);
    const filteredRows = originalHotels.filter((item) => {
      return (
        item.name.toLowerCase().includes(searchText.toLowerCase()) ||
        item.code.toLowerCase().includes(searchText.toLowerCase())
      );
    });
    onsetPage(0);
    setHotels(filteredRows);
  };

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const onOpenDrawer = (type) => {
    handleClose();
    onDrawerToggle(type, true, selectedValue.hotel);
  };

  const deleteHotel = async (e) => {
    try {
      await deleteHotelById(selectedValue.id);
      dispatch(fetchAllProperties());
      notificationManager('success', 'Hotel deleted successfully');
    } catch (e) {
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'Hotel deletion failed',
      );
    } finally {
      handleClose();
    }
  };

  const handleCellMenuClick = (event, hotel) => {
    setSelectedValue({
      id: hotel.id,
      name: hotel.name,
      title: '',
      description: '',
      status: false,
      anchorEl: event.currentTarget,
      hotel: hotel,
    });
  };

  const handleClose = () => {
    setSelectedValue(initialState);
  };

  const handleDeletePopupOpen = () => {
    setSelectedValue({
      id: selectedValue.id,
      name: selectedValue.name,
      title: 'Are you sure you want to delete',
      description:
        'All data under this Property (Users, property information, etc) will be deleted. This action cannot be reversed',
      status: true,
      anchorEl: null,
    });
  };

  const onViewDetails = (hotel, e) => {
    if (e.target.className === 'MuiTableCell-root MuiTableCell-body') {
      onViewToggle(HOTEL_VIEW, true, hotel);
    }
  };

  const onClickView = (e) => {
    if (selectedValue && selectedValue.hotel) {
      onViewToggle(HOTEL_VIEW, true, selectedValue.hotel);
    }
  };

  const updateUiBuilderStatus = async () => {
    const curStatus = selectedValue.hotel.isUIBuilderEnabled;
    try {
      await updateHotelUIBuilder(selectedValue.hotel, !curStatus);
      dispatch(fetchAllProperties());
      notificationManager(
        'success',
        `Ui Builder ${curStatus ? 'disabled' : 'enabled'} successfully `,
      );
      handleClose();
    } catch (e) {
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'Ui Builder status update failed',
      );
    }
  };

  const updateDeviceModuleStatus = async (endpoint) => {
    const curStatus = selectedValue.hotel.isDevicesEnabled;
    try {
      await updateHotelDeviceModule(selectedValue.hotel, !curStatus, endpoint);
      dispatch(fetchAllProperties());
      notificationManager('success', `Device ${curStatus ? 'disabled' : 'enabled'} successfully `);
    } catch (e) {
      console.error(e);
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'Device module status update failed',
      );
    } finally {
      handleClose();
    }
  };

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {selectedValue?.id && selectedValue?.deviceEnabling && (
        <DeviceEnablingPopup
          currentStatus={selectedValue}
          onCancel={handleClose}
          onEnabling={(url) => updateDeviceModuleStatus(url)}
        />
      )}
      {selectedValue.id && selectedValue.status === true && (
        <DeletePopupModel
          deletePopUp={selectedValue}
          onDelete={deleteHotel}
          onDeleteCancel={handleClose}
        />
      )}
      <div className='row mx-2 d-flex'>
        <div className='col col-lg-4'>
          <TextField
            className='w-100'
            onChange={(e) => {
              setSearchText(e.target.value);
              setSearchButton(e?.target?.value ? false : true);
            }}
            value={tempSearchText}
            label='Search'
            InputProps={
              searchButton && {
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }
          />
        </div>
        <div className='col-12 col-lg-8 pt-3 pt-lg-0 d-flex justify-content-end'>
          {permissions[RBAC_TYPE_HOTELS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS &&
            !isPropertyAdmin && (
              <Button
                variant='contained'
                color='primary'
                size='large'
                startIcon={<AddCircle />}
                onClick={(e) => onOpenDrawer(HOTEL_CREATE)}
              >
                Add Hotel
              </Button>
            )}
        </div>
      </div>

      <div className='row mx-2 d-flex mt-4'>
        <TableContainer>
          <Table className='list-table' aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Hotel Name</TableCell>
                <TableCell>Hotel Code</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Location</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rowsPerPage > 0
                ? hotels.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : hotels
              ).map((hotel, i) => (
                <TableRow
                  key={i}
                  className='border-white pointer'
                  onClick={(e) => {
                    onViewDetails(hotel, e);
                  }}
                >
                  <TableCell>{hotel.name}</TableCell>
                  <TableCell>{hotel.code}</TableCell>
                  <TableCell>{FormattingUtils.getBrandName(brands, hotel.brandId)}</TableCell>
                  <TableCell>{FormattingUtils.getGroupName(groups, hotel.groupId)}</TableCell>
                  <TableCell>
                    {hotel.location.city}, {hotel.location.country}{' '}
                  </TableCell>
                  <TableCell align='right'>
                    {permissions[RBAC_TYPE_HOTELS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                      <Button
                        id={hotel.id + 'kebab-menu'}
                        aria-controls={hotel.id + 'kebab-menu'}
                        aria-haspopup='true'
                        onClick={(e) => {
                          handleCellMenuClick(e, hotel);
                        }}
                      >
                        <MoreHoriz />
                      </Button>
                    )}
                    <Menu
                      id={i}
                      anchorEl={selectedValue?.anchorEl}
                      disableRestoreFocus
                      MenuListProps={{ autoFocusItem: false }}
                      keepMounted
                      open={Boolean(selectedValue.anchorEl) && selectedValue.id === hotel.id}
                      onClose={handleClose}
                      className='shadow'
                    >
                      <MenuItem
                        className='pe-4'
                        onClick={(e) => {
                          onOpenDrawer(HOTEL_EDIT);
                        }}
                      >
                        <CreateIcon className='me-2 action-menu-icon' />
                        Edit
                      </MenuItem>
                      <MenuItem
                        className='pe-4'
                        onClick={(e) => {
                          onClickView(e);
                        }}
                      >
                        <VisibilityIcon className='me-2 action-menu-icon' />
                        View
                      </MenuItem>
                      {!isPropertyAdmin && (
                        <MenuItem
                          className='pe-4'
                          onClick={(e) => {
                            handleDeletePopupOpen();
                          }}
                        >
                          <DeleteForeverIcon className='me-2 action-menu-icon' />
                          Delete
                        </MenuItem>
                      )}
                      {userRole === 'Super Admin' && (
                        <MenuItem
                          className='pe-4'
                          onClick={(e) => {
                            updateUiBuilderStatus(hotel);
                          }}
                        >
                          {hotel.isUIBuilderEnabled ? (
                            <>
                              <ToggleOnIcon className='me-2 action-menu-icon' />
                              Disable Ui Builder
                            </>
                          ) : (
                            <>
                              <ToggleOffIcon className='me-2 action-menu-icon color-gray' />
                              Enable Ui Builder
                            </>
                          )}
                        </MenuItem>
                      )}
                      {userRole === 'Super Admin' && (
                        <MenuItem
                          className='pe-4'
                          onClick={(e) => {
                            hotel.isDevicesEnabled
                              ? updateDeviceModuleStatus('')
                              : setSelectedValue({
                                  ...selectedValue,
                                  deviceEnabling: true,
                                });
}}
                        >
                          {hotel.isDevicesEnabled ? (
                            <>
                              <ToggleOnIcon className='me-2 action-menu-icon' />
                              Disable TV Device
                            </>
                          ) : (
                            <>
                              <ToggleOffIcon className='me-2 action-menu-icon color-gray' />
                              Enable TV Device
                            </>
                          )}
                        </MenuItem>
                      )}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className='paginator'>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  count={hotels.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default BrandList;
